import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Heart, ArrowRight, PenLine, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const copy = {
  en: {
    headline: 'Now it\'s your turn…',
    subtext: 'This message was created on Say It NOW. You can send one too — while they\'re still here.',
    sendBack: 'Send one back',
    writeOwn: 'Write your own',
    sendAnon: 'Send anonymously',
    remember: 'People remember messages like this forever.',
  },
  es: {
    headline: 'Ahora es tu turno…',
    subtext: 'Este mensaje fue creado en Say It NOW. Tú también puedes enviar uno — mientras aún están aquí.',
    sendBack: 'Enviar uno de vuelta',
    writeOwn: 'Escribe el tuyo',
    sendAnon: 'Enviar anónimamente',
    remember: 'Las personas recuerdan mensajes como este para siempre.',
  },
  fr: {
    headline: 'À votre tour…',
    subtext: 'Ce message a été créé sur Say It NOW. Vous pouvez en envoyer un aussi — tant qu\'ils sont encore là.',
    sendBack: 'Répondre par un message',
    writeOwn: 'Écrire le vôtre',
    sendAnon: 'Envoyer anonymement',
    remember: 'On se souvient de ces messages pour toujours.',
  },
  pt: {
    headline: 'Agora é a sua vez…',
    subtext: 'Esta mensagem foi criada no Say It NOW. Você também pode enviar uma — enquanto eles ainda estão aqui.',
    sendBack: 'Enviar de volta',
    writeOwn: 'Escreva a sua',
    sendAnon: 'Enviar anonimamente',
    remember: 'As pessoas lembram de mensagens assim para sempre.',
  },
  ar: {
    headline: 'الآن دورك…',
    subtext: 'تم إنشاء هذه الرسالة على Say It NOW. يمكنك إرسال واحدة أيضًا — بينما هم لا يزالون هنا.',
    sendBack: 'أرسل واحدة بالمقابل',
    writeOwn: 'اكتب رسالتك',
    sendAnon: 'أرسل بشكل مجهول',
    remember: 'يتذكر الناس رسائل مثل هذه إلى الأبد.',
  },
  ja: {
    headline: '今度はあなたの番…',
    subtext: 'このメッセージはSay It NOWで作成されました。あなたも送ることができます — まだ間に合ううちに。',
    sendBack: '返事を送る',
    writeOwn: '自分のメッセージを書く',
    sendAnon: '匿名で送る',
    remember: 'こういうメッセージは一生忘れられません。',
  },
  ko: {
    headline: '이제 당신 차례입니다…',
    subtext: '이 메시지는 Say It NOW에서 만들어졌습니다. 당신도 보낼 수 있습니다 — 아직 곁에 있을 때.',
    sendBack: '답장 보내기',
    writeOwn: '나만의 메시지 쓰기',
    sendAnon: '익명으로 보내기',
    remember: '이런 메시지는 영원히 기억됩니다.',
  },
  id: {
    headline: 'Sekarang giliranmu…',
    subtext: 'Pesan ini dibuat di Say It NOW. Kamu juga bisa mengirim satu — selagi mereka masih di sini.',
    sendBack: 'Kirim balasan',
    writeOwn: 'Tulis pesanmu sendiri',
    sendAnon: 'Kirim secara anonim',
    remember: 'Orang-orang mengingat pesan seperti ini selamanya.',
  },

  hi: {
    headline: 'अब आपकी बारी है…',
    subtext: 'यह संदेश Say It NOW पर बनाया गया था। आप भी एक भेज सकते हैं — जब तक वे अभी भी यहाँ हैं।',
    sendBack: 'एक वापस भेजें',
    writeOwn: 'अपना खुद का लिखें',
    sendAnon: 'गुमनाम रूप से भेजें',
    remember: 'लोग ऐसे संदेशों को हमेशा याद रखते हैं।',
  },
};

interface RecipientViralCTAProps {
  source?: string;
  senderName?: string;
  senderReferralCode?: string;
}

export function RecipientViralCTA({ source = 'announcement_view', senderName, senderReferralCode }: RecipientViralCTAProps) {
  const { language } = useLanguage();
  const t = lc(copy, language);
  const tracked = useRef(false);

  // Store sender's referral code for attribution
  useEffect(() => {
    if (senderReferralCode && !tracked.current) {
      tracked.current = true;
      const existing = localStorage.getItem('sayitnow_ref');
      if (!existing) {
        localStorage.setItem('sayitnow_ref', senderReferralCode);
      }
      supabase.from('events' as any).insert({
        event_name: 'recipient_viral_cta_viewed',
        properties: { source, has_ref: !!senderReferralCode },
      }).then(() => {});
    }
  }, [senderReferralCode, source]);

  const refParam = senderReferralCode ? `&ref=${senderReferralCode}` : '';

  const trackClick = (action: string) => {
    supabase.from('events' as any).insert({
      event_name: 'recipient_viral_cta_clicked',
      properties: { source, action, ref: senderReferralCode || null },
    }).then(() => {});
  };

  // Pre-fill recipient as sender for "send back"
  const sendBackUrl = senderName
    ? `/try?recipient=${encodeURIComponent(senderName)}${refParam}`
    : `/try?${refParam.replace('&', '')}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="py-10 px-4"
    >
      <div className="max-w-md mx-auto text-center space-y-6">
        {/* Divider */}
        <div className="flex items-center justify-center gap-3">
          <div className="h-px w-12 bg-border" />
          <Heart className="h-4 w-4 text-primary/40" />
          <div className="h-px w-12 bg-border" />
        </div>

        {/* Headline */}
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-foreground">
            {t.headline}
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
            {t.subtext}
          </p>
        </div>

        {/* 3 Action Buttons */}
        <div className="space-y-2.5 pt-1">
          {/* Send one back — primary */}
          {senderName && (
            <a href={sendBackUrl} onClick={() => trackClick('send_back')} className="block">
              <Button size="lg" className="w-full gap-2.5 rounded-xl shadow-md shadow-primary/15 h-12">
                <Heart className="h-4 w-4" fill="currentColor" />
                {t.sendBack}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </a>
          )}

          {/* Write your own */}
          <a href={`/try?${refParam.replace('&', '')}`} onClick={() => trackClick('write_own')} className="block">
            <Button
              variant={senderName ? 'outline' : 'default'}
              size="lg"
              className="w-full gap-2.5 rounded-xl h-12"
            >
              <PenLine className="h-4 w-4" />
              {t.writeOwn}
            </Button>
          </a>

          {/* Send anonymously */}
          <a href={`/try?anon=true${refParam}`} onClick={() => trackClick('send_anon')} className="block">
            <Button variant="ghost" size="lg" className="w-full gap-2.5 rounded-xl h-11 text-muted-foreground">
              <EyeOff className="h-4 w-4" />
              {t.sendAnon}
            </Button>
          </a>
        </div>

        {/* Soft emotional line */}
        <p className="text-xs text-muted-foreground/60 italic pt-2">
          {t.remember}
        </p>
      </div>
    </motion.div>
  );
}